import type { Auswahleintrag } from './Auswahlfeld'
import { bisVorStilllegung, mitBis } from './Bisvermerk'

/**
 * Was eine Auswahl anbietet — **ohne das Stillgelegte** (7.4 Abschnitt 34.5,
 * R774, R1447).
 *
 * **Entschieden war es seit dem 2026-09-25 für alle Stammdaten**, gebaut nur
 * im Import, im Spiegel und im Erhebungspaket. In den Auswahllisten der
 * Oberfläche wirkte es bei keiner Art: Ein stillgelegtes Konto stand weiter in
 * jeder Auswahl der Buchungsmaske.
 *
 * **Eine Stelle für jede Auswahlliste.** Ein Stammdatum wird über
 * `ausStammdatum` zum Eintrag und bringt dabei sein *Stillgelegt ab* mit;
 * Auswahlfeld und Kontofeld fragen hier, was sie zeigen.
 *
 * **Wer einen Eintrag selbst baut, reicht es weiter** — aus dem
 * Stammdatenkatalog, dem Vorrat der Buchungsmaske, den Zielfeldern des
 * Imports. `Stilllegung.test.tsx` meldet jede Stelle, die einen Eintrag aus
 * einem Stammdatum baut, ohne es mitzugeben, und nennt die bewussten
 * Ausnahmen mit Grund.
 */

/**
 * Das *Stillgelegt ab* aus den Werten eines Listeneintrags — die
 * Stammdatenliste trägt es dort wie jedes andere Feld (R1447).
 */
export function stillgelegtAus(werte: Record<string, string> | null | undefined): number | null {
  const wert = werte?.stillgelegt_ab
  return wert ? Number(wert) : null
}

/** Der laufende Monat als Periode, etwa 202610. */
export function laufenderMonat(heute = new Date()): number {
  return heute.getFullYear() * 100 + heute.getMonth() + 1
}

/**
 * Gilt ein Stammdatum mit diesem *Stillgelegt ab* in dieser Periode noch?
 * Dieselbe Lesart wie `Stilllegung.Gilt` im Rechenkern: „ab 202603" heißt im
 * Februar ja, im März nicht mehr; leer heißt gilt.
 */
export function gilt(stillgelegtAb: number | null | undefined, periode: number): boolean {
  return stillgelegtAb == null || periode < stillgelegtAb
}

/**
 * Die Einträge, die in dieser Periode gelten — **kennt die Auswahl keine
 * Periode, zählt der laufende Monat** (R1447).
 *
 * Ein schon eingetragener Wert bleibt stehen und sagt, seit wann er
 * stillgelegt ist: Verschwände er, stünde das Feld leer da, und niemand wüsste,
 * was eingetragen war.
 *
 * Steht nichts Stillgelegtes in der Liste, kommt sie unverändert zurück —
 * dieselbe Reihe, damit nichts darüber neu rechnet.
 */
export function angeboten(
  eintraege: Auswahleintrag[],
  wert: string,
  periode: number = laufenderMonat(),
): Auswahleintrag[] {
  if (!eintraege.some((e) => !gilt(e.stillgelegtAb, periode))) {
    return eintraege
  }

  return eintraege.flatMap((e) =>
    gilt(e.stillgelegtAb, periode)
      ? [e]
      : e.wert === wert
        ? [{ ...e, text: mitBis(e.text, bisVorStilllegung(e.stillgelegtAb!)) }]
        : [],
  )
}
